import { AlertTriangle } from "lucide-react";

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/base/sheet";

import {
  formatEpoch,
  type BusinessProfile,
  type ParsedStripeErrors,
} from "../types";
import { CapabilityPill } from "./CapabilitiesCard";
import { merchantCapabilities } from "./capabilities";
import { OverlayRow, OverlaySection } from "./OverlaySection";

/**
 * Why each capability is where it is, and what Stripe is still waiting on.
 *
 * The card answers "can they take money"; this answers "and what has to
 * happen before they can".
 */
export function CapabilitiesSheet({
  open,
  onOpenChange,
  business,
  stripeErrors,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  business: BusinessProfile;
  stripeErrors: ParsedStripeErrors | null;
}) {
  const capabilities = merchantCapabilities(business, stripeErrors);
  const currentlyDue = stripeErrors?.currentlyDue ?? [];
  const restricted = stripeErrors?.restrictedCapabilities ?? [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full gap-0 p-0 sm:max-w-[563px]">
        <SheetHeader className="border-border border-b px-6 py-4">
          <SheetTitle className="text-foreground text-lg font-semibold">
            Capabilities
          </SheetTitle>
        </SheetHeader>

        <div className="overflow-y-auto">
          {capabilities.map((capability) => (
            <OverlaySection key={capability.name} title={capability.name}>
              <OverlayRow label="Status">
                <CapabilityPill capability={capability} />
              </OverlayRow>
              <OverlayRow label="Reason">{capability.detail}</OverlayRow>
            </OverlaySection>
          ))}

          <OverlaySection title="Payouts detail">
            <OverlayRow label="Track stage">
              {business.payoutsStage ?? null}
            </OverlayRow>
            <OverlayRow label="Schedule">
              {business.payoutState?.interval ? (
                <span className="capitalize">
                  {business.payoutState.interval}
                </span>
              ) : null}
            </OverlayRow>
            <OverlayRow label="Paused for review">
              {business.payoutState?.pausedForReview ? "Yes" : "No"}
            </OverlayRow>
          </OverlaySection>

          <OverlaySection title="Stripe requirements">
            {currentlyDue.length === 0 && restricted.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                Nothing outstanding with Stripe.
              </p>
            ) : (
              <>
                {stripeErrors?.currentDeadlineStamp != null && (
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-amber-700">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Deadline {formatEpoch(stripeErrors.currentDeadlineStamp)}
                  </p>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {restricted.map((cap) => (
                    <span
                      key={cap}
                      className="rounded bg-red-50 px-2 py-0.5 font-mono text-xs text-red-700 ring-1 ring-red-200 ring-inset"
                    >
                      {cap}
                    </span>
                  ))}
                  {currentlyDue.map((item) => (
                    <span
                      key={item}
                      className="rounded bg-amber-50 px-2 py-0.5 font-mono text-xs text-amber-800 ring-1 ring-amber-200 ring-inset"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </>
            )}
          </OverlaySection>
        </div>
      </SheetContent>
    </Sheet>
  );
}
